import { FormDialog } from '@/components/FormDialog'
import { UserSelect } from '@/components/UserSelect'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useGetUsers } from '@/features/users/hooks/useUser'
import { UserRole } from '@/features/users/types/user'
import { isApiError } from '@/utils/error'
import { showError, showSuccess } from '@/utils/toast'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreateVendor, useUpdateVendor } from '../hooks/useVendors'
import type { Vendor } from '../types/vendor'

export function VendorDialog({
  open,
  onOpenChange,
  vendor,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  vendor: Vendor | null
}) {
  const { t } = useTranslation()
  const createVendor = useCreateVendor()
  const updateVendor = useUpdateVendor()
  const { data: vendorUsers = [], isLoading: isLoadingVendorUsers } = useGetUsers(UserRole.Vendor)

  const [form, setForm] = useState({ name: '', contactId: '', phone: '', email: '' })
  const isEdit = !!vendor

  useEffect(() => {
    if (vendor) {
      setForm({
        name: vendor.name,
        contactId: vendor.contactId || '',
        phone: vendor.phone || '',
        email: vendor.email || '',
      })
    } else {
      setForm({ name: '', contactId: '', phone: '', email: '' })
    }
  }, [vendor])

  const isLoading = createVendor.isPending || updateVendor.isPending || isLoadingVendorUsers
  const isInvalid = !form.name || !form.contactId

  function handleSubmit() {
    if (isInvalid) return

    const options = {
      onSuccess: () => {
        onOpenChange(false)
        showSuccess(isEdit ? t('vendors.updateSuccess') : t('vendors.createSuccess'))
      },
      onError: (error: unknown) => {
        if (isApiError(error)) showError(error.message)
      },
    }

    if (isEdit && vendor) {
      updateVendor.mutate({ id: vendor.id, vendor: form }, options)
    } else {
      createVendor.mutate(form, options)
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? t('vendors.editVendor') : t('vendors.addVendor')}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      isInvalid={isInvalid}
      contentTestId="vendor-dialog"
      submitTestId="vendor-form-submit"
    >
      <div className="flex flex-col gap-4 pt-2">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('vendors.companyName')} *</Label>
          <Input
            data-testid="vendor-form-name"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder={t('vendors.companyPlaceholder')}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('vendors.contact')} *</Label>
          <UserSelect
            testId="vendor-form-contact-trigger"
            users={vendorUsers}
            value={form.contactId}
            onValueChange={contactId => setForm({ ...form, contactId })}
            disabled={isLoadingVendorUsers}
            placeholder={isLoadingVendorUsers ? t('common.loading') : t('vendors.selectContact')}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">{t('vendors.phone')}</Label>
            <Input
              data-testid="vendor-form-phone"
              value={form.phone}
              onChange={e => setForm({ ...form, phone: e.target.value })}
              className="h-9 bg-secondary/50 text-xs"
              placeholder="02-xxx-xxxx"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">{t('vendors.email')}</Label>
            <Input
              data-testid="vendor-form-email"
              type="email"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              className="h-9 bg-secondary/50 text-xs"
              placeholder="sales@abc.com"
            />
          </div>
        </div>
      </div>
    </FormDialog>
  )
}
